import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CourseEditor } from '../CourseEditor';
import { getCourseById, getUserProfile, getOrganization, logout } from '../../services/firebase';
import { Course, UserProfile, Organization } from '../../types';
import { Icons } from '../ui/Icons';

export const CourseEditorRoute: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | undefined>(undefined);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!courseId) {
        setError('No course ID provided');
        setLoading(false);
        return;
      }

      try {
        // Load user profile first (required for editing)
        const userId = localStorage.getItem('userId');
        if (!userId) {
          setError('You must be logged in to edit courses');
          setLoading(false);
          return;
        }

        const userProfile = await getUserProfile(userId);
        if (!userProfile) {
          setError('User profile not found');
          setLoading(false);
          return;
        }
        setUser(userProfile);

        if (userProfile.orgId) {
          const org = await getOrganization(userProfile.orgId);
          setOrganization(org);
        }

        // Handle new course creation
        if (courseId === 'new') {
          setCourse(undefined);
          setLoading(false);
          return;
        }

        // Load course data for existing course
        const courseData = await getCourseById(courseId);
        if (!courseData) {
          setError('Course not found');
          setLoading(false);
          return;
        }

        // Verify user has permission to edit this course
        if (courseData.authorId !== userProfile.uid && courseData.orgId !== userProfile.orgId) {
          setError('You do not have permission to edit this course');
          setLoading(false);
          return;
        }

        setCourse(courseData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Failed to load course');
        setLoading(false);
      }
    };

    loadData();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.X className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Course not found or access denied'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <CourseEditor
      user={user}
      organization={organization}
      existingCourse={course}
      onLogout={logout}
      onClose={() => navigate('/dashboard')}
      onSave={() => {
        navigate('/dashboard');
      }}
    />
  );
};
